import { Fragment, useEffect, useState } from "react";
import {
  Box,
  Button,
  Link,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DropzoneProps } from "~/components/dropzone";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { Command } from "~/components/guides/components";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  ActionType,
  ClientIntegrationType,
  GetClientIntegrationDocument,
  GetIntegrationsSummaryDocument,
  IntegrationType,
  useCreateClientIntegrationMutation,
  useTriggerActionLazyQuery,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { useSnackbar } from "notistack";
import { AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import { IntegrationRecommendedPolicies } from "../../components/IntegrationRecommendedPolicies";
import { IntegrationAddHeader } from "../../headers/IntegrationAddHeader";
import { getError } from "~/lib/handle-error";
import {
  EMAIL_PATTERN,
  helperTextStyles,
  ValidationMessage,
} from "../../utils/integrationValidationUtils";
import useGenerateIntegrationName from "../../utils/integrationsUtils";
import { UpdateFlowData } from "../../utils/integrationsTypes";
import { setDocumentTitle } from "~/lib/commonUtils";
import { HostedIntegrationDropzoneUpload } from "~/pages/integrations/hosted-integrations/forms/components/HostedIntegrationDropzoneUpload";
import { HostedIntegrationDropzoneSuccess } from "~/pages/integrations/hosted-integrations/forms/components/HostedIntegrationDropzoneSuccess";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

type GoogleWorkspaceFormInput = {
  integrationName: string;
  customerId: string;
  impersonatedUserEmail: string;
  serviceAccount: string | null;
};

const defaultValues: GoogleWorkspaceFormInput = {
  integrationName: "",
  customerId: "",
  impersonatedUserEmail: "",
  serviceAccount: null,
};

export function GoogleWorkspaceIntegrationForm({
  scope,
  updateFlow,
}: {
  scope: SpaceScope | OrganizationScope;
  updateFlow?: UpdateFlowData;
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [view, setView] = useState<"setup" | "add policies">("setup");
  const defaultIntegrationName = useGenerateIntegrationName({ scope });
  const [refreshKey, setRefreshKey] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    getFieldState,
    formState: { errors, isValid, isSubmitSuccessful },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      ...defaultValues,
      integrationName: defaultIntegrationName,
    },
  });

  const [createIntegration] = useCreateClientIntegrationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const integrationMrn = data.createClientIntegration.integration.mrn;
      triggerClientIntegrationScan({
        variables: { input: { mrn: integrationMrn, type: ActionType.RunScan } },
      });
    },
    refetchQueries: [
      {
        query: GetIntegrationsSummaryDocument,
        variables: { input: { spaceMrn: scope.mrn } },
      },
    ],
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [triggerClientIntegrationScan] = useTriggerActionLazyQuery({
    onError(error) {
      console.error("%c Error Scheduling scan on creation", "color: tomato");
      console.error(error.message);
    },
  });

  const handleDropzoneRefresh = () => {
    setValue("serviceAccount", "");
    setRefreshKey(true);
  };

  useEffect(() => {
    if (updateFlow) {
      if (
        updateFlow.integration.configurationOptions?.__typename ===
        "GoogleWorkspaceConfigurationOptions"
      ) {
        reset({
          integrationName: updateFlow.integration.name,
          customerId: updateFlow.integration.configurationOptions.customerId,
          impersonatedUserEmail:
            updateFlow.integration.configurationOptions.impersonatedUserEmail,
        });
      }
    }
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful && !updateFlow) {
      reset(defaultValues);
    }
  }, [isSubmitSuccessful]);

  const [serviceAccount] = watch(["serviceAccount"]);

  const onSubmit: SubmitHandler<GoogleWorkspaceFormInput> = async (data) => {
    const googleWorkspaceConfigurationOptions = {
      customerId: data.customerId,
      impersonatedUserEmail: data.impersonatedUserEmail,
      serviceAccount: data.serviceAccount,
    };

    try {
      if (updateFlow) {
        const integrationId = updateFlow.integration.mrn.split("/").pop();
        const mrn = getIntegrationMrn({ scope, integrationId });
        await updateIntegration({
          variables: {
            input: {
              mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.GoogleWorkspace,
              configurationOptions: {
                googleWorkspaceConfigurationOptions,
              },
            },
          },
        });

        enqueueSnackbar("Successfully updated configuration", {
          variant: "success",
        });
        navigate(
          `/space/integrations/google_workspace/${integrationId}/?${scope.params}`,
        );
      } else {
        await createIntegration({
          variables: {
            input: {
              spaceMrn: scope.mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.GoogleWorkspace,
              longLivedToken: false,
              configurationOptions: {
                googleWorkspaceConfigurationOptions,
              },
            },
          },
        });
        setView("add policies");
      }
    } catch (e) {
      const msg = getError(e);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const onDropAccepted: DropzoneProps["onDropAccepted"] = async (files) => {
    setValue("serviceAccount", await files[0].text());
    setRefreshKey(false);
  };

  setDocumentTitle(["GoogleWorkspace", "Integrations Setup"]);

  return (
    <Fragment>
      {view === "setup" ? (
        <Fragment>
          <IntegrationAddHeader
            {...{ type: IntegrationType.GoogleWorkspace }}
          />
          <Box>
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Step 1 */}
              <Box pb={4}>
                <Command
                  number={1}
                  options={{
                    fontSize: { xs: 16 },
                    dotColor: theme.palette.background.lightest,
                  }}
                >
                  Choose an integration name
                </Command>

                <Box pl={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, mt: 2 }}
                  >
                    Please choose a descriptive name that lets you easily
                    identify your integration.
                  </Typography>
                  <Controller
                    name="integrationName"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        sx={{
                          background: theme.palette.code.background,
                          borderRadius: 1,
                          color: "text.primary",
                          ...helperTextStyles,
                        }}
                        placeholder="Your integration name..."
                        error={Boolean(errors.integrationName)}
                        helperText={
                          Boolean(errors.integrationName) && (
                            <ValidationMessage error={errors.integrationName} />
                          )
                        }
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* Step 2 */}
              <Box pb={4}>
                <Command
                  number={2}
                  options={{
                    fontSize: { xs: 16 },
                    dotColor: theme.palette.background.lightest,
                  }}
                >
                  Enter your customer ID
                </Command>

                <Box pl={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, mt: 2 }}
                  >
                    Find your customer ID in your Google Workspace account
                    settings. To learn more, read the{" "}
                    <Link
                      href="https://support.google.com/a/answer/10070793"
                      target="_blank"
                      rel="noopener"
                    >
                      Google documentation
                    </Link>
                    .
                  </Typography>
                  <Controller
                    name="customerId"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        sx={{
                          background: theme.palette.code.background,
                          borderRadius: 1,
                          color: "text.primary",
                          ...helperTextStyles,
                        }}
                        placeholder="XXXXXXXXX"
                        error={Boolean(errors.customerId)}
                        helperText={
                          getFieldState("customerId").isTouched &&
                          Boolean(errors.customerId) && (
                            <ValidationMessage
                              error={errors.customerId}
                              integrationTypeId="google_workspace"
                            />
                          )
                        }
                      />
                    )}
                  />
                </Box>
              </Box>
              {/* Step 3 */}
              <Box pb={4}>
                <Command
                  number={3}
                  options={{
                    fontSize: { xs: 16 },
                    dotColor: theme.palette.background.lightest,
                  }}
                >
                  Mandated user's email
                </Command>

                <Box pl={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, mt: 2 }}
                  >
                    Specify on whose behalf the integration should act. You can
                    choose any user that is part of your workspace, as long as
                    they have Super Admin permissions. To learn more, read the{" "}
                    <Link
                      href="https://docs.cloud.google.com/resource-manager/docs/super-admin-best-practices"
                      target="_blank"
                      rel="noopener"
                    >
                      Google documentation
                    </Link>
                    .
                  </Typography>
                  <Controller
                    name="impersonatedUserEmail"
                    control={control}
                    rules={{
                      required: true,
                      validate: {
                        isEmail: (value) =>
                          Boolean(value.match(EMAIL_PATTERN)) ||
                          "Not a valid email",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        sx={{
                          background: theme.palette.code.background,
                          borderRadius: 1,
                          color: "text.primary",
                          ...helperTextStyles,
                        }}
                        placeholder="yourworkspacemember@googlemail.com"
                        error={Boolean(errors.impersonatedUserEmail)}
                        helperText={
                          getFieldState("impersonatedUserEmail").isTouched &&
                          Boolean(errors.impersonatedUserEmail) && (
                            <ValidationMessage
                              error={errors.impersonatedUserEmail}
                              integrationTypeId="google_workspace"
                            />
                          )
                        }
                      />
                    )}
                  />
                </Box>
              </Box>
              {/* step 4 */}
              <Box pb={4}>
                <Command
                  number={4}
                  options={{
                    fontSize: { xs: 16 },
                    dotColor: theme.palette.background.lightest,
                  }}
                >
                  Provide your Google service account config
                </Command>
                <Box pl={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, mt: 2 }}
                  >
                    Create a service account in GCP with permissions to access
                    your workspace and upload the json here. To learn more, read
                    the{" "}
                    <Link
                      href="https://mondoo.com/docs/infra/saas/google_workspace"
                      target="_blank"
                      rel="noopener"
                    >
                      Mondoo documentation
                    </Link>
                    .
                  </Typography>
                  <AnimatePresence mode="wait">
                    {/* if we're in creation flow and don't have a serviceAccount */}
                    {/* if we're in updateFlow, and user has requested to refreshKey */}
                    {((!serviceAccount && !updateFlow) ||
                      (refreshKey && updateFlow)) && (
                      <HostedIntegrationDropzoneUpload
                        key="upload"
                        fileType=".json"
                        accept={{ "application/json": [] }}
                        onDropAccepted={onDropAccepted}
                      />
                    )}

                    {/* if we're in creation flow and already have a serviceAccount */}
                    {/* if we're in the update flow and have not requested a refresh OR have already refreshed */}
                    {((serviceAccount && !updateFlow) ||
                      (!refreshKey && updateFlow)) && (
                      <HostedIntegrationDropzoneSuccess
                        key="success"
                        {...{
                          handleDropzoneRefresh,
                          isUpdateFlow: Boolean(updateFlow),
                        }}
                      />
                    )}
                  </AnimatePresence>
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "end" }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  endIcon={<ChevronRightIcon />}
                  // setValue doesn't trigger the required validation so we're doing it manually here
                  disabled={updateFlow ? !isValid : !isValid || !serviceAccount}
                >
                  {updateFlow ? "update configuration" : "start scanning"}
                </Button>
              </Box>
            </form>
          </Box>
        </Fragment>
      ) : (
        <IntegrationRecommendedPolicies
          {...{ scope }}
          filterTypes={["google-workspace"]}
        />
      )}
    </Fragment>
  );
}
